import { createFileRoute } from "@tanstack/react-router";
import { Download, Settings } from "lucide-react";
import { Suspense } from "react";
import { EmptyState } from "@/client/components/empty-state";
import { PageHeader } from "@/client/components/page-header";
import { apiQuery, useApiSuspenseQuery } from "@/client/lib/api";
import { ensurePage, pageHead } from "@/client/lib/page";
import { HorariosClient } from "@/client/modules/assistente/horarios-client";
import Loading from "@/client/modules/clinica/loading";
import { ProceduresClient } from "@/client/modules/clinica/procedures-client";
import { ProfessionalsClient } from "@/client/modules/clinica/professionals-client";
import { TeamClient } from "@/client/modules/clinica/team-client";
import { FEATURES } from "@/shared/features";

export const Route = createFileRoute("/app/clinica")({
  loader: ({ context }) =>
    Promise.all([
      ensurePage(context.queryClient, apiQuery("clinica", "page", {})),
      // Grade de horários só existe com o assistente ligado.
      FEATURES.assistente ? ensurePage(context.queryClient, apiQuery("assistente", "horarios", {})) : null,
    ]),
  head: () => pageHead("Clínica"),
  pendingComponent: Loading,
  component: ClinicaPage,
});

function ClinicaPage() {
  const { data } = useApiSuspenseQuery("clinica", "page", {});
  return (
    <div className="mx-auto flex max-w-7xl flex-col gap-6">
      <PageHeader title="Clínica" description="Equipe, procedimentos e backup dos dados." />
      <TeamClient rows={data.team} currentUserId={data.currentUserId} />
      <ProceduresClient rows={data.procedures} />
      {FEATURES.assistente && (
        <Suspense fallback={<div className="h-40 animate-pulse rounded-xl border border-slate-200 bg-slate-100" />}>
          <HorariosClient />
        </Suspense>
      )}
      {/* Backup operacional — baixa uma planilha Excel pra guardar no PC. */}
      <section className="rounded-xl border border-slate-200 bg-white p-5">
        <div className="mb-3 flex items-center gap-2">
          <Download className="h-4 w-4 text-brand-600" />
          <h2 className="text-sm font-semibold text-slate-800">Backup dos dados</h2>
        </div>
        <p className="mb-4 max-w-2xl text-sm text-slate-500">
          Baixa uma planilha Excel com pacientes, atendimentos, despesas, retornos e estoque. Guarde o arquivo no
          computador da clínica de vez em quando. Não inclui prontuário/evolução (dado clínico privado) nem imagens.
        </p>
        <a
          href="/api/v1/clinica/files/backup.xlsx"
          className="inline-flex items-center gap-1.5 rounded-lg bg-brand-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-800"
        >
          <Download className="h-4 w-4" />
          Baixar backup (Excel)
        </a>
      </section>
      {data.showProfessionals ? (
        <ProfessionalsClient rows={data.professionals} />
      ) : (
        <EmptyState
          icon={Settings}
          title="Profissionais"
          description="A gestão de profissionais aparece aqui quando a capacidade multi-profissional está ligada."
        />
      )}
    </div>
  );
}
